import Constants from "@-/com.npmjs.expo-constants";
import { NativeModules, Platform } from "@-/com.npmjs.react-native";
import { ENV } from "@-/com.npmjs.varlock/env";
import { isLocalDevServerHostname } from "@-/dev.improve.haniverse.lib.dev-server-host";

const ABSOLUTE_URL_REGEX = /^[a-z][a-z\d+.-]*:/i;
const DEFAULT_ANDROID_PUBLIC_ASSET_ORIGIN = "http://localhost:8081";
const DEFAULT_IOS_PUBLIC_ASSET_ORIGIN = "http://localhost:4574";

type NativeSourceCodeModule = {
  getConstants?: () => { scriptURL?: unknown };
  scriptURL?: unknown;
};

function expoPublicAssetOriginFromHostUri(hostUri: string | undefined): string | null {
  if (hostUri == null || hostUri.length === 0) {
    return null;
  }
  const host = hostUri.split(":")[0];
  if (host == null || host.length === 0) {
    return null;
  }
  if (isLocalDevServerHostname(host)) {
    return `http://${host}:4574`;
  }
  return `https://${host}`;
}

function expoPublicAssetOriginFromScriptUrl(scriptURL: string | undefined): string | null {
  if (scriptURL == null || scriptURL.length === 0) {
    return null;
  }
  try {
    const origin = new URL(scriptURL).origin;
    return origin === "null" ? null : origin;
  } catch {
    return null;
  }
}

function readNativeSourceCodeScriptUrl(): string | undefined {
  const sourceCode: unknown = NativeModules.SourceCode;
  if (sourceCode == null || typeof sourceCode !== "object") {
    return undefined;
  }
  const typedSourceCode: NativeSourceCodeModule = sourceCode;
  const scriptURL = typedSourceCode.getConstants?.().scriptURL ?? typedSourceCode.scriptURL;
  return typeof scriptURL === "string" && scriptURL.length > 0 ? scriptURL : undefined;
}

/**
 * A standalone native bundle (a release or OTA build: `assets://` / `file://` script URL, no
 * dev-server `hostUri`) has no dev server to serve `public/`. Its public files are deployed with the
 * web export on the same EAS Hosting origin that serves `/api` (`EXPO_PUBLIC_BETTER_AUTH_URL`), so
 * resolve them there. Falling through to `localhost` made every meaning image fail on staging
 * Android ("CLEARTEXT communication to localhost not permitted"), and that failed load crashed
 * expo-image's Glide callback, blanking the app (staging run 35942723611, android shard 3).
 * A loopback URL (an intentional local-backend release build) keeps the dev fallback below.
 */
function deployedPublicAssetOrigin(): string | null {
  const configured = ENV.EXPO_PUBLIC_BETTER_AUTH_URL?.trim();
  if (configured == null || configured.length === 0) {
    return null;
  }
  try {
    const url = new URL(configured);
    if (isLocalDevServerHostname(url.hostname)) {
      return null;
    }
    return url.origin;
  } catch {
    return null;
  }
}

function fallbackExpoPublicAssetOrigin(): string {
  return Platform.OS === "android"
    ? DEFAULT_ANDROID_PUBLIC_ASSET_ORIGIN
    : DEFAULT_IOS_PUBLIC_ASSET_ORIGIN;
}

export function resolveExpoPublicAssetUrl(urlPath: string): string {
  if (ABSOLUTE_URL_REGEX.test(urlPath) || !urlPath.startsWith("/")) {
    return urlPath;
  }
  if (Platform.OS === "web") {
    return urlPath;
  }
  const origin =
    expoPublicAssetOriginFromScriptUrl(readNativeSourceCodeScriptUrl()) ??
    expoPublicAssetOriginFromHostUri(Constants.expoConfig?.hostUri) ??
    deployedPublicAssetOrigin() ??
    fallbackExpoPublicAssetOrigin();
  return `${origin}${urlPath}`;
}
